import { useCallback, useEffect, useRef, useState } from 'react';
import type { NextRouter } from 'next/router';

export const CHAT_CHANNELS = ['', 'WHATSAPP', 'SMS', 'EXTERNAL_AGENT', 'CALENDAR_MEETINGS'] as const;
export const MEETINGS_CHAT_CHANNEL = 'CALENDAR_MEETINGS';
/**
 * The "Groups" tab: Symphony group chats only, behind `symphonyGroupChat`. Not a server channel — a group can be any
 * channel — so the list is read as All and narrowed on the client (`isGroupsFilter` / `serverChannel`). Same chip on
 * iOS and Android, after the channel chips.
 */
export const GROUPS_CHAT_FILTER = 'GROUPS';
/** The meetings tab exists only inside the `symphonyMeetingsAgent` release; Groups only with group chat on. */
export function visibleChatChannels(meetingsEnabled: boolean, groupsEnabled = false): readonly string[] {
  const channels: string[] = meetingsEnabled ? [...CHAT_CHANNELS] : CHAT_CHANNELS.filter((channel) => channel !== MEETINGS_CHAT_CHANNEL);
  return groupsEnabled ? [...channels, GROUPS_CHAT_FILTER] : channels;
}
/** The channel the server is asked for: Groups reads All. */
export const serverChannel = (channel: string) => (channel === GROUPS_CHAT_FILTER ? '' : channel);

/** Groups fill: keep paging All until this many group rows are loaded (about a screen)… */
export const GROUPS_FILL_ROWS = 20;
/** …or this many pages are loaded, whichever comes first. */
export const GROUPS_FILL_MAX_PAGES = 10;
/**
 * How the Groups tab pages. The server cannot filter groups, so the tab narrows All on the client, and a run of pages
 * with few or no group rows would leave the list empty with more to come: the ordinary near-the-end trigger never fires
 * on a list that does not fill the screen. So:
 * - `fill`: fetch the next page on its own, with the loading row showing and no empty state;
 * - `scroll`: a screen's worth is loaded, ordinary infinite scroll;
 * - `done`: no more pages, or the page budget is spent: nothing fetches by itself, and an empty list says so.
 * Same rule and numbers on iOS (`GroupsPaging`) and Android (`groupsPaging`).
 */
export type GroupsPaging = 'fill' | 'scroll' | 'done';
export function groupsPaging(shownRows: number, pagesLoaded: number, hasMore: boolean): GroupsPaging {
  if (!hasMore) return 'done';
  if (shownRows >= GROUPS_FILL_ROWS) return 'scroll';
  return pagesLoaded >= GROUPS_FILL_MAX_PAGES ? 'done' : 'fill';
}
export const stringParam = (value: string | string[] | undefined) => typeof value === 'string' ? value : '';
/** A link or restored URL naming a tab the reader cannot see lands on All, never on a hidden tab. */
export function chatsFilters(query: NextRouter['query'], meetingsEnabled = false, groupsEnabled = false) {
  const value = stringParam(query.channel);
  return { search: stringParam(query.q).slice(0, 200), channel: visibleChatChannels(meetingsEnabled, groupsEnabled).includes(value) ? value : '' };
}
const key = (filters: { search: string; channel: string }) => JSON.stringify(filters);

/** Filters live in the URL so hydration, browser Back and a shared link restore the same list. */
export function useChatsFilters(router: NextRouter, basePath: string, meetingsEnabled = false, groupsEnabled = false) {
  const [filters, setFilters] = useState(() => chatsFilters(router.query, meetingsEnabled, groupsEnabled));
  const pending = useRef<string | null>(null);
  useEffect(() => {
    // A browser history navigation supersedes an outstanding shallow replace from typing.
    const pop = () => { pending.current = null; };
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);
  const fromUrl = key(chatsFilters(router.query, meetingsEnabled, groupsEnabled));
  useEffect(() => {
    if (!router.isReady || (pending.current && pending.current !== fromUrl)) return;
    pending.current = null;
    setFilters(JSON.parse(fromUrl) as typeof filters);
  }, [router.isReady, fromUrl]);
  const update = useCallback((next: typeof filters) => {
    setFilters(next);
    pending.current = key(next);
    const { q: _q, channel: _channel, ...rest } = router.query;
    const query = { ...rest, ...(next.search ? { q: next.search } : {}), ...(next.channel ? { channel: next.channel } : {}) };
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(query)) if (typeof value === 'string') params.set(name, value);
    void router.replace({ pathname: '/chats', query }, `${basePath}/chats${params.size ? `?${params}` : ''}`, { shallow: true, scroll: false })
      .catch(() => { pending.current = null; });
  }, [router, basePath]);
  return { ...filters, setSearch: (search: string) => update({ ...filters, search }), setChannel: (channel: string) => update({ ...filters, channel }) };
}
